import {
  CaretDownIcon,
  CaretRightIcon
} from "@phosphor-icons/react";
import type { ReactNode } from "react";
import "./ui/file-tree-v2.css";

export function ResourceTreeAction({
  label,
  onClick,
  children,
  disabled = false
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      className="flex h-5 w-5 cursor-pointer items-center justify-center rounded bg-transparent text-kumo-subtle hover:bg-kumo-fill hover:text-kumo-default disabled:cursor-default disabled:opacity-35"
    >
      {children}
    </button>
  );
}

export function ResourceTreeRow({
  depth,
  label,
  detail,
  icon,
  expanded = false,
  expandable = false,
  onClick,
  onDoubleClick,
  actions,
  title
}: {
  depth: number;
  label: string;
  detail?: string;
  icon: ReactNode;
  expanded?: boolean;
  expandable?: boolean;
  onClick?: () => void;
  onDoubleClick?: () => void;
  actions?: ReactNode;
  title?: string;
}) {
  return (
    <div
      role="treeitem"
      data-component="file-tree-v2"
      aria-expanded={expandable ? expanded : undefined}
      title={title}
      className="group flex min-h-6 w-full items-center pr-1 text-xs text-kumo-subtle hover:bg-kumo-elevated/60 hover:text-kumo-default"
    >
      <button
        type="button"
        data-slot="file-tree-v2-row"
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-1 bg-transparent py-[3px] text-left"
        style={{ paddingLeft: 6 + depth * 12 }}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
      >
        <span className="flex h-3 w-3 shrink-0 items-center justify-center">
          {expandable ? (
            expanded ? (
              <CaretDownIcon size={10} weight="bold" />
            ) : (
              <CaretRightIcon size={10} weight="bold" />
            )
          ) : null}
        </span>
        <span className="flex h-[15px] w-[15px] shrink-0 items-center justify-center">
          {icon}
        </span>
        <span className="truncate text-kumo-default">{label}</span>
        {detail ? (
          <span className="truncate text-[10px] text-kumo-subtle">{detail}</span>
        ) : null}
      </button>
      {actions ? (
        <div className="flex shrink-0 items-center opacity-0 group-hover:opacity-100 group-focus-within:opacity-100">
          {actions}
        </div>
      ) : null}
    </div>
  );
}

export function formatResourceBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes < 1024 * 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  return `${(bytes / (1024 * 1024 * 1024 * 1024)).toFixed(1)} TB`;
}
